import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NeniTiendaTab } from "@/components/NeniTiendaTab";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Download, MapPin, Wallet, TrendingUp, Search, Store as StoreIcon,
  Send, Package, MessageCircle,
} from "lucide-react";

export default function NeniDashboard() {
  const [tab, setTab] = useState("catalogo");
  const [productos, setProductos] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const [plazaId, setPlazaId] = useState("");
  const [q, setQ] = useState("");
  const [ganancias, setGanancias] = useState(null);
  const [ordenes, setOrdenes] = useState([]);

  const loadCatalogo = () => {
    const params = plazaId ? { plaza_id: plazaId } : {};
    api.get("/nenis/catalogo", { params }).then(r => setProductos(r.data.productos)).catch(e => toast.error(formatApiError(e)));
  };
  const loadPlazas = () => api.get("/nenis/plazas-cobertura").then(r => setPlazas(r.data.plazas));
  const loadGanancias = () => api.get("/nenis/mis-ganancias").then(r => setGanancias(r.data));
  const loadOrdenes = () => api.get("/nenis/mis-ordenes").then(r => setOrdenes(r.data.ordenes));

  useEffect(() => { loadPlazas(); loadGanancias(); loadOrdenes(); }, []);
  useEffect(() => { loadCatalogo(); /* eslint-disable-next-line */ }, [plazaId]);

  const filtered = useMemo(() => productos.filter(p =>
    !q || p.nombre.toLowerCase().includes(q.toLowerCase()) || (p.tienda?.nombre_local || "").toLowerCase().includes(q.toLowerCase())
  ), [productos, q]);

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-zinc-500">Panel NENI</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">NENIS PLACE</h1>
          </div>
          {ganancias && (
            <div className="flex gap-3">
              <div className="bg-white border border-zinc-200 rounded-sm px-4 py-2 text-right">
                <div className="text-xs uppercase tracking-wider text-zinc-500">Disponible</div>
                <div className="font-display font-black text-xl">${ganancias.disponible.toFixed(2)}</div>
              </div>
              <div className="bg-[#D4FF00] rounded-sm px-4 py-2 text-right">
                <div className="text-xs uppercase tracking-wider">Total</div>
                <div className="font-display font-black text-xl">${ganancias.total_lifetime.toFixed(2)}</div>
              </div>
            </div>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="rounded-sm bg-white border border-zinc-200 p-1 h-11">
            <TabsTrigger value="catalogo" data-testid="neni-tab-catalogo" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">Catálogo</TabsTrigger>
            <TabsTrigger value="mitienda" data-testid="neni-tab-mitienda" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">🌐 Mi Tienda Online</TabsTrigger>
            <TabsTrigger value="ordenes" data-testid="neni-tab-ordenes" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">Mis Órdenes</TabsTrigger>
            <TabsTrigger value="ganancias" data-testid="neni-tab-ganancias" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">Ganancias</TabsTrigger>
            <TabsTrigger value="cobertura" data-testid="neni-tab-cobertura" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">Plazas & Cobertura</TabsTrigger>
          </TabsList>

          {/* Mi Tienda Online */}
          <TabsContent value="mitienda" className="mt-6">
            <NeniTiendaTab catalogo={productos} />
          </TabsContent>

          {/* Catálogo */}
          <TabsContent value="catalogo" className="mt-6">
            {/* Info banner: WhatsApp coordination con gerentes */}
            <div className="mb-5 rounded-sm border-2 border-[#25D366]/40 bg-[#25D366]/5 p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-sm grid place-items-center bg-[#25D366] text-white">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold tracking-tight">Coordina directo por WhatsApp con cada tienda</div>
                  <p className="text-sm text-zinc-700 mt-1 leading-relaxed max-w-3xl">
                    En cada tarjeta de producto verás un botón <b className="text-[#128C7E]">"Contactar tienda"</b> que abre WhatsApp con el dueño o gerente del local.
                    Úsalo para <b>acordar precios especiales, descuentos, comisiones extra o descuentos por volumen</b> antes de publicar tu venta.
                    Tú decides tu margen final: el precio base que ves es solo el mínimo que la tienda quiere recibir — negociar más ganancia depende de ti.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm p-4 flex flex-wrap items-center gap-3 mb-5">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input data-testid="neni-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar producto o tienda…" className="pl-9 h-11 rounded-sm" />
              </div>
              <Select value={plazaId || "all"} onValueChange={v => setPlazaId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="neni-plaza-filter" className="w-56 rounded-sm h-11"><SelectValue placeholder="Todas las plazas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las plazas</SelectItem>
                  {plazas.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {filtered.length === 0 ? (
              <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center text-zinc-500">
                Sin productos disponibles. Pide a los comercios que activen NENIS PLACE.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map(p => <NeniProductoCard key={p.id} producto={p} onOrder={loadOrdenes} />)}
              </div>
            )}
          </TabsContent>

          {/* Órdenes */}
          <TabsContent value="ordenes" className="mt-6">
            <div className="bg-white border border-zinc-200 rounded-sm">
              {ordenes.length === 0 ? (
                <div className="p-16 text-center text-zinc-500">Aún no tienes órdenes. Vende tu primer producto desde el catálogo.</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {ordenes.map(o => (
                    <div key={o.id} data-testid={`neni-orden-${o.id}`} className="p-4 flex flex-wrap items-center gap-4">
                      <img src={o.producto_imagen} alt={o.producto_nombre} className="w-14 h-14 rounded-sm object-cover bg-zinc-100" />
                      <div className="flex-1 min-w-[200px]">
                        <div className="font-display font-semibold tracking-tight">{o.producto_nombre}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Cliente: {o.buyer_name} · {o.buyer_phone}</div>
                        <div className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-bold">${o.precio_final.toFixed(2)}</div>
                        <div className="text-xs text-emerald-600 font-medium">+${o.comision.toFixed(2)}</div>
                      </div>
                      <Badge className={
                        o.estado === "Entregado_Y_Cobrado" ? "bg-emerald-100 text-emerald-700 border-0" :
                        o.estado === "Enviado" ? "bg-amber-100 text-amber-700 border-0" :
                        o.estado === "Cancelada" ? "bg-red-100 text-red-700 border-0" :
                        "bg-zinc-100 text-zinc-700 border-0"
                      }>{o.estado.replace(/_/g, " ")}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Ganancias */}
          <TabsContent value="ganancias" className="mt-6">
            {ganancias && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <MetricCard label="Total comisiones" value={`$${ganancias.total_lifetime.toFixed(2)}`} color="#FF3366" Icon={TrendingUp} />
                <MetricCard label="Pendientes (en camino)" value={`$${ganancias.pending.toFixed(2)}`} color="#F59E0B" Icon={Package} />
                <MetricCard label="Disponible para retiro" value={`$${ganancias.disponible.toFixed(2)}`} color="#10B981" Icon={Wallet} />
              </div>
            )}
            <div className="mt-6 bg-white border border-zinc-200 rounded-sm">
              <div className="p-4 border-b border-zinc-100 font-display font-bold tracking-tight">Historial de comisiones liquidadas</div>
              {ganancias?.ledger?.length ? (
                <div className="divide-y divide-zinc-100 text-sm">
                  {ganancias.ledger.map(l => (
                    <div key={l.id} className="p-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{l.producto_nombre}</div>
                        <div className="text-xs text-zinc-500">{new Date(l.settled_at || l.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="font-mono font-bold text-emerald-600">+${l.comision.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="p-8 text-center text-zinc-400 text-sm">Sin liquidaciones aún</div>}
            </div>
          </TabsContent>

          {/* Cobertura */}
          <TabsContent value="cobertura" className="mt-6">
            <CoverageMap plazas={plazas} />

            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plazas.map(p => (
                <div key={p.id} data-testid={`plaza-cov-${p.id}`} className="bg-white border border-zinc-200 rounded-sm p-5 card-lift">
                  <div className="flex items-center gap-3">
                    {p.logo_url ? <img src={p.logo_url} alt={p.nombre} className="w-10 h-10 object-contain" /> : <div className="w-10 h-10 rounded-sm grid place-items-center" style={{ background: p.color_primary }}><StoreIcon className="w-5 h-5 text-white" /></div>}
                    <div>
                      <div className="font-display font-bold tracking-tight">{p.nombre}</div>
                      <div className="text-xs text-zinc-500">{p.n_tiendas} comercios · {p.n_productos} productos</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.direccion}</div>
                  <div className="mt-3 pt-3 border-t border-zinc-100">
                    <div className="overline text-zinc-500">Cobertura máxima</div>
                    <div className="font-display font-black text-2xl tracking-tighter">{p.radio_entrega_km} km</div>
                    <p className="text-[11px] text-zinc-500 mt-1">Solo puedes citar a tus clientes dentro de este radio desde el mall.</p>
                  </div>
                </div>
              ))}
              {plazas.length === 0 && (
                <div className="col-span-full border border-dashed border-zinc-300 rounded-sm p-16 text-center text-zinc-500">
                  Aún no hay plazas participando en NENIS PLACE.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color, Icon }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6">
      <Icon className="w-5 h-5" style={{ color }} />
      <div className="mt-3 font-display font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function NeniProductoCard({ producto, onOrder }) {
  const [markup, setMarkup] = useState(50);
  const precioBase = producto.precio_base_nenis || producto.precio;
  const precioFinal = precioBase + markup;
  const comision = markup;

  const bulkDownload = async () => {
    try {
      toast.loading("Preparando ZIP…", { id: "zip" });
      const zip = new JSZip();
      const imgs = [producto.imagen_url].filter(Boolean);
      for (let i = 0; i < imgs.length; i++) {
        try {
          const r = await fetch(imgs[i]);
          const blob = await r.blob();
          const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
          zip.file(`${producto.nombre.replace(/[^a-z0-9]/gi, "_")}_${i + 1}.${ext}`, blob);
        } catch { /* skip */ }
      }
      const descripcion = `${producto.nombre}\n\n${producto.descripcion || ""}\n\nPrecio de venta sugerido: $${precioFinal.toFixed(2)}\nTu ganancia: $${comision.toFixed(2)}\n\n#${(producto.tienda?.nombre_local || "").replace(/\s/g, "")}`;
      zip.file("descripcion.txt", descripcion);
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `${producto.nombre.replace(/[^a-z0-9]/gi, "_")}.zip`);
      toast.success("ZIP descargado", { id: "zip" });
    } catch (e) {
      toast.error("Error al generar ZIP", { id: "zip" });
    }
  };

  const whatsappShare = () => {
    const msg = `🛍️ *${producto.nombre}*\n\n${producto.descripcion || ""}\n\n💰 *Precio: $${precioFinal.toFixed(2)}*\n\n📸 Foto: ${producto.imagen_url}\n\n¡Escríbeme para reservar! 🚀`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Abriendo WhatsApp…");
  };

  const contactarTienda = () => {
    const wa = producto.tienda?.whatsapp_gerente;
    if (!wa) { toast.error("Esta tienda aún no configuró su WhatsApp"); return; }
    const clean = wa.replace(/\D/g, "");
    const msg = `Hola! Soy NENI en en 90 MINUTOS. Quiero acordar precio y comisión para el producto: *${producto.nombre}* (precio base $${precioBase.toFixed(2)}). ¿Podemos coordinar?`;
    window.open(`https://wa.me/${clean}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    toast.success("Abriendo chat con la tienda…");
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col card-lift" data-testid={`neni-p-${producto.id}`}>
      <div className="relative aspect-square bg-zinc-100">
        {producto.imagen_url && <img src={producto.imagen_url} alt={producto.nombre} className="w-full h-full object-cover" />}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          <button onClick={bulkDownload} data-testid={`neni-download-${producto.id}`} className="bg-white/90 backdrop-blur rounded-sm px-2 py-1 text-xs font-medium hover:bg-white flex items-center gap-1">
            <Download className="w-3 h-3" /> ZIP
          </button>
          <button onClick={whatsappShare} data-testid={`neni-whatsapp-${producto.id}`} className="bg-[#25D366] text-white rounded-sm px-2 py-1 text-xs font-medium hover:bg-[#128C7E] flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> WhatsApp
          </button>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="overline text-zinc-500">{producto.tienda?.nombre_local}</div>
        <div className="font-display font-semibold tracking-tight mt-0.5 line-clamp-1">{producto.nombre}</div>
        <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{producto.descripcion}</p>

        <div className="mt-3 pt-3 border-t border-zinc-100 text-xs">
          <div className="flex justify-between text-zinc-500">
            <span>Precio base</span><span className="font-mono">${precioBase.toFixed(2)}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-zinc-500 text-[11px]">Tu margen</span>
            <Input value={markup} onChange={e => setMarkup(parseFloat(e.target.value) || 0)} type="number" className="h-7 rounded-sm text-xs" />
          </div>
          <div className="mt-2 flex justify-between">
            <span className="text-zinc-500">Vendes en</span><span className="font-display font-black">${precioFinal.toFixed(2)}</span>
          </div>
          <div className="mt-1 flex justify-between text-emerald-600">
            <span className="font-medium">Ganas</span><span className="font-mono font-bold">+${comision.toFixed(2)}</span>
          </div>
        </div>

        <SubmitOrderDialog producto={producto} precioFinal={precioFinal} onOrder={onOrder} />
        {producto.tienda?.whatsapp_gerente ? (
          <button
            onClick={contactarTienda}
            data-testid={`neni-contact-store-${producto.id}`}
            className="mt-2 w-full rounded-sm border border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 h-9 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
            title="Coordina precio, descuento o comisión antes de vender"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Contactar tienda para negociar
          </button>
        ) : (
          <div className="mt-2 text-[10px] text-amber-600 text-center italic">Tienda sin WhatsApp configurado</div>
        )}
      </div>
    </div>
  );
}

/** OpenStreetMap + Leaflet coverage map. Loads Leaflet from CDN once. */
function CoverageMap({ plazas }) {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    // Inject Leaflet CSS + JS from CDN if not present
    const cssId = "leaflet-css";
    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    let cancelled = false;
    const ensureLeaflet = () => new Promise((resolve) => {
      if (window.L) return resolve(window.L);
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.async = true;
      script.onload = () => resolve(window.L);
      document.body.appendChild(script);
    });

    (async () => {
      const L = await ensureLeaflet();
      if (cancelled || !ref.current) return;
      // Reset any previous map instance
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

      const validPlazas = plazas.filter(p => p.lat && p.lng);
      if (validPlazas.length === 0) return;

      const map = L.map(ref.current, { zoomControl: true });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "© OpenStreetMap",
      }).addTo(map);

      const bounds = L.latLngBounds([]);
      validPlazas.forEach(p => {
        const color = p.color_primary || "#FF3366";
        L.circle([p.lat, p.lng], {
          radius: (p.radio_entrega_km || 5) * 1000,
          color, weight: 2, fillColor: color, fillOpacity: 0.15,
        }).bindPopup(`<b>${p.nombre}</b><br/>${p.direccion || ""}<br/>Cobertura: ${p.radio_entrega_km} km<br/>${p.n_tiendas} comercios · ${p.n_productos} productos`).addTo(map);
        L.marker([p.lat, p.lng]).addTo(map).bindPopup(`<b>${p.nombre}</b>`);
        bounds.extend([p.lat, p.lng]);
      });
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [plazas]);

  if (plazas.length === 0) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
      <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
        <div>
          <div className="overline text-zinc-500">Mapa en vivo</div>
          <div className="font-display font-bold text-lg tracking-tight">Radios de cobertura</div>
        </div>
        <div className="text-xs text-zinc-500">{plazas.length} {plazas.length === 1 ? "plaza" : "plazas"} activas</div>
      </div>
      <div ref={ref} data-testid="coverage-map" className="w-full h-[420px] bg-zinc-100" style={{ zIndex: 0 }} />
      <div className="p-3 text-[11px] text-zinc-500 border-t border-zinc-100">
        💡 Cada círculo muestra la zona máxima donde puedes citar a tus clientes desde esa plaza. Fuera del círculo, el repartidor no puede entregar.
      </div>
    </div>
  );
}

function SubmitOrderDialog({ producto, precioFinal, onOrder }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ buyer_name: "", buyer_phone: "", buyer_address: "", buyer_lat: 0, buyer_lng: 0, talla: "", color: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const geo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => {
      set("buyer_lat", pos.coords.latitude); set("buyer_lng", pos.coords.longitude);
      toast.success("GPS del punto de entrega capturado");
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/nenis/ordenes", { producto_id: producto.id, precio_final: precioFinal, ...f });
      toast.success("¡Orden enviada! Ganancia: $" + (precioFinal - (producto.precio_base_nenis || producto.precio)).toFixed(2));
      setOpen(false);
      setF({ buyer_name: "", buyer_phone: "", buyer_address: "", buyer_lat: 0, buyer_lng: 0, talla: "", color: "" });
      onOrder?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`neni-sell-${producto.id}`} className="mt-3 w-full rounded-sm bg-[#FF3366] hover:bg-[#E62958] h-10">
          <Send className="w-3.5 h-3.5 mr-1.5" /> Enviar orden
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle>Datos de tu cliente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Nombre del comprador</Label>
            <Input data-testid="ord-buyer-name" value={f.buyer_name} onChange={e => set("buyer_name", e.target.value)} required className="mt-1 rounded-sm h-10" />
          </div>
          <div>
            <Label>Teléfono</Label>
            <Input data-testid="ord-buyer-phone" value={f.buyer_phone} onChange={e => set("buyer_phone", e.target.value)} required className="mt-1 rounded-sm h-10" />
          </div>
          <div>
            <Label>Dirección / Punto de entrega</Label>
            <Textarea data-testid="ord-buyer-addr" value={f.buyer_address} onChange={e => set("buyer_address", e.target.value)} required rows={2} className="mt-1 rounded-sm" />
            <Button type="button" onClick={geo} variant="outline" size="sm" className="mt-2 rounded-sm text-xs" data-testid="ord-geo">
              <MapPin className="w-3 h-3 mr-1" /> Usar GPS ({f.buyer_lat.toFixed(3)}, {f.buyer_lng.toFixed(3)})
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Talla" value={f.talla} onChange={e => set("talla", e.target.value)} className="rounded-sm h-10" />
            <Input placeholder="Color" value={f.color} onChange={e => set("color", e.target.value)} className="rounded-sm h-10" />
          </div>
          <div className="p-3 bg-zinc-50 rounded-sm text-sm space-y-1">
            <div className="flex justify-between"><span>Precio final</span><span className="font-mono font-bold">${precioFinal.toFixed(2)}</span></div>
            <div className="flex justify-between text-emerald-600"><span>Tu comisión</span><span className="font-mono font-bold">+${(precioFinal - (producto.precio_base_nenis || producto.precio)).toFixed(2)}</span></div>
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="ord-submit" disabled={loading} className="bg-[#FF3366] hover:bg-[#E62958]">
              {loading ? "Enviando…" : "Confirmar orden"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
